// SPDX-License-Identifier: AGPL-3.0-or-later

import {CustomStatusDisplay} from '@app/features/app/components/shared/custom_status_display/CustomStatusDisplay';
import type {AvatarDecorationSurface} from '@app/features/cosmetics/components/AvatarUltraLayer';
import Guilds from '@app/features/guild/state/Guilds';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {UserProfileModal} from '@app/features/user/components/modals/UserProfileModal';
import {UserProfileBadges} from '@app/features/user/components/popouts/UserProfileBadges';
import {
	UserProfileMembershipInfo,
	UserProfilePreviewBio,
	UserProfileTimezoneInfo,
} from '@app/features/user/components/popouts/UserProfileShared';
import styles from '@app/features/user/components/profile/ProfilePreview.module.css';
import {ProfileCardBanner} from '@app/features/user/components/profile/profile_card/ProfileCardBanner';
import {ProfileCardContent} from '@app/features/user/components/profile/profile_card/ProfileCardContent';
import {ProfileCardFooter} from '@app/features/user/components/profile/profile_card/ProfileCardFooter';
import {ProfileCardLayout} from '@app/features/user/components/profile/profile_card/ProfileCardLayout';
import {ProfileCardUserInfo} from '@app/features/user/components/profile/profile_card/ProfileCardUserInfo';
import {ProfileMessageField} from '@app/features/user/components/profile/profile_card/ProfileMessageField';
import {useProfileCardDisplayState} from '@app/features/user/components/profile/useProfileCardDisplayState';
import {useAutoplayExpandedProfileAnimations} from '@app/features/user/hooks/useAutoplayExpandedProfileAnimations';
import type {Profile} from '@app/features/user/models/Profile';
import type {User} from '@app/features/user/models/User';
import type {CustomStatus} from '@app/features/user/state/CustomStatus';
import UserProfileStore from '@app/features/user/state/UserProfile';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import type {ProfilePreviewOverrides} from '@app/features/user/utils/ProfileDisplayUtils';
import {type BadgeSettings, createMockProfile} from '@app/features/user/utils/ProfileUtils';
import {PublicUserFlags} from '@fluxer/constants/src/UserConstants';
import type {CosmeticAssetDataResponse} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';
import type {NameStyle, ThemeColors} from '@fluxer/schema/src/domains/user/PlusAppearanceSchemas';
import type {UserProfile} from '@fluxer/schema/src/domains/user/UserResponseSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo} from 'react';

const PROFILE_PREVIEW_PRESS_ENTER_TO_OPEN_FULL_PREVIEW_DESCRIPTOR = msg({
	message: 'Profile preview (press Enter to open full preview)',
	comment: 'Label in the user settings profile preview.',
});

interface ProfilePreviewProps {
	user: User;
	previewAvatarUrl?: string | null;
	previewBannerUrl?: string | null;
	hasClearedAvatar?: boolean;
	hasClearedBanner?: boolean;
	previewBio?: string | null;
	previewPronouns?: string | null;
	previewAccentColor?: number | null;
	previewTimezoneOffset?: number | null;
	previewGlobalName?: string | null;
	previewNameStyle?: NameStyle | null;
	previewThemeColors?: ThemeColors | null;
	previewNick?: string | null;
	guildId?: string | null;
	guildMember?: GuildMember | null;
	guildMemberProfile?: UserProfile | null;
	previewBadgeSettings?: BadgeSettings;
	ignoreGuildAvatarInPreview?: boolean;
	ignoreGuildBannerInPreview?: boolean;
	showMembershipInfo?: boolean;
	showMessageButton?: boolean;
	showPreviewLabel?: boolean;
	previewCustomStatus?: CustomStatus | null;
	previewProfileEffect?: CosmeticAssetDataResponse | null;
	decorationSurface?: AvatarDecorationSurface;
}

export const ProfilePreview: React.FC<ProfilePreviewProps> = observer(
	({
		user,
		previewAvatarUrl,
		previewBannerUrl,
		hasClearedAvatar,
		hasClearedBanner,
		previewBio,
		previewPronouns,
		previewAccentColor,
		previewTimezoneOffset,
		previewGlobalName,
		previewNameStyle,
		previewThemeColors,
		previewNick,
		guildId,
		guildMember,
		guildMemberProfile,
		previewBadgeSettings,
		ignoreGuildAvatarInPreview,
		ignoreGuildBannerInPreview,
		showMembershipInfo = true,
		showMessageButton = true,
		showPreviewLabel = true,
		previewCustomStatus,
		previewProfileEffect,
		decorationSurface = 'profile',
	}) => {
		const {i18n} = useLingui();
		const previewOverrides = useMemo<ProfilePreviewOverrides>(
			() => ({
				previewAvatarUrl,
				previewBannerUrl,
				hasClearedAvatar,
				hasClearedBanner,
				ignoreGuildAvatar: ignoreGuildAvatarInPreview,
				ignoreGuildBanner: ignoreGuildBannerInPreview,
			}),
			[
				previewAvatarUrl,
				previewBannerUrl,
				hasClearedAvatar,
				hasClearedBanner,
				ignoreGuildAvatarInPreview,
				ignoreGuildBannerInPreview,
			],
		);
		const isCommunityProfile = Boolean(guildId && guildMemberProfile);
		const previewBadgeHidden = previewBadgeSettings?.premium_badge_hidden;
		const previewUser = useMemo(() => {
			const globalName = previewGlobalName !== undefined ? previewGlobalName : user.globalName;
			// Micup: the profile badge row reads PREMIUM_BADGE and the package bits, so the unsaved "hide the premium badges"
			// switch clears them.
			let flags = user.flags;
			if (previewBadgeHidden === true) {
				flags &= ~(
					PublicUserFlags.PREMIUM_BADGE |
					PublicUserFlags.PREMIUM_BADGE_WHITE |
					PublicUserFlags.PREMIUM_BADGE_BLUE |
					PublicUserFlags.PREMIUM_BADGE_GOLD |
					PublicUserFlags.PREMIUM_BADGE_SINGLE
				);
			} else if (previewBadgeHidden === false && (user.premiumType ?? 0) > 0) {
				flags |= PublicUserFlags.PREMIUM_BADGE;
			}
			if (isCommunityProfile) {
				return user.withUpdates({global_name: globalName, flags});
			}
			const bio = previewBio !== undefined ? previewBio : user.bio;
			const pronouns = previewPronouns !== undefined ? previewPronouns : user.pronouns;
			return user.withUpdates({
				bio,
				pronouns,
				global_name: globalName,
				flags,
				...(previewNameStyle !== undefined && {name_style: previewNameStyle}),
				...(previewThemeColors !== undefined && {theme_colors: previewThemeColors}),
			});
		}, [
			user,
			previewBio,
			previewPronouns,
			previewGlobalName,
			previewNameStyle,
			previewThemeColors,
			isCommunityProfile,
			previewBadgeHidden,
		]);
		const mockProfile = useMemo(() => {
			if (isCommunityProfile && guildId && guildMemberProfile) {
				const globalProfile = createMockProfile(previewUser, {
					previewTimezoneOffset,
					previewBadgeSettings,
					previewProfileEffect,
				});
				return globalProfile.withGuildId(guildId).withUpdates({
					guild_member: guildMember?.toJSON(),
					guild_member_profile: {
						bio: previewBio !== undefined ? previewBio : guildMemberProfile.bio,
						banner: previewBannerUrl || guildMemberProfile.banner,
						pronouns: previewPronouns !== undefined ? previewPronouns : guildMemberProfile.pronouns,
						accent_color: previewAccentColor !== undefined ? previewAccentColor : guildMemberProfile.accent_color,
					},
				});
			}
			return createMockProfile(previewUser, {
				previewBannerUrl,
				hasClearedBanner,
				previewBio,
				previewPronouns,
				previewAccentColor,
				previewTimezoneOffset,
				previewBadgeSettings,
				previewProfileEffect,
				previewThemeColors,
			});
		}, [
			previewUser,
			previewBannerUrl,
			hasClearedBanner,
			previewBio,
			previewPronouns,
			previewAccentColor,
			previewTimezoneOffset,
			previewBadgeSettings,
			previewProfileEffect,
			previewThemeColors,
			guildId,
			guildMember,
			guildMemberProfile,
			isCommunityProfile,
		]);
		const shouldAutoplayProfileAnimations = useAutoplayExpandedProfileAnimations();
		const {
			avatarUrl: finalAvatarUrl,
			hoverAvatarUrl: finalHoverAvatarUrl,
			bannerUrl: finalBannerUrl,
			hoverBannerUrl: finalHoverBannerUrl,
			accentColor,
		} = useProfileCardDisplayState({
			user,
			profile: mockProfile,
			guildId,
			guildMember,
			guildMemberProfile: mockProfile.getGuildMemberProfile() ?? guildMemberProfile,
			previewOverrides,
			accentUser: previewUser,
		});
		const openMockProfile = useCallback(() => {
			ModalCommands.push(
				modal(() => (
					<UserProfileModal
						userId={user.id}
						guildId={guildId || undefined}
						disableEditProfile={true}
						previewOverrides={{
							previewAvatarUrl,
							previewBannerUrl,
							previewAccentColor,
							hasClearedAvatar,
							hasClearedBanner,
							ignoreGuildAvatar: ignoreGuildAvatarInPreview,
							ignoreGuildBanner: ignoreGuildBannerInPreview,
						}}
						previewUser={previewUser}
						previewProfile={mockProfile}
						data-flx="user.profile.profile-preview.open-mock-profile.user-profile-modal"
					/>
				)),
			);
		}, [
			user.id,
			guildId,
			previewAvatarUrl,
			previewBannerUrl,
			previewAccentColor,
			hasClearedAvatar,
			hasClearedBanner,
			ignoreGuildAvatarInPreview,
			ignoreGuildBannerInPreview,
			previewUser,
			mockProfile,
		]);
		const pronouns = previewPronouns !== undefined ? previewPronouns : user.pronouns;
		const displayName =
			previewNick || (guildId ? NicknameUtils.getNickname(previewUser, guildId) : previewUser.displayName);
		const bannerColor = accentColor;
		const selectedGuild = guildId ? Guilds.getGuild(guildId) : null;
		const hasPreviewStatus = previewCustomStatus !== undefined;
		const handlePreviewKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				openMockProfile();
			}
		};
		return (
			<FocusRing offset={-2} data-flx="user.profile.profile-preview.focus-ring">
				<div
					className={styles.previewInteractive}
					role="group"
					aria-label={i18n._(PROFILE_PREVIEW_PRESS_ENTER_TO_OPEN_FULL_PREVIEW_DESCRIPTOR)}
					onKeyDown={handlePreviewKeyDown}
					data-flx="user.profile.profile-preview.preview-interactive.preview-key-down"
				>
					<ProfileCardLayout
						themeColors={previewUser.themeColors ?? null}
						showPreviewLabel={showPreviewLabel}
						profileEffect={
							previewProfileEffect !== undefined
								? previewProfileEffect
								: UserProfileStore.getProfile(user.id)?.profileEffect
						}
						data-flx="user.profile.profile-preview.profile-card-layout"
					>
						<ProfileCardBanner
							bannerUrl={finalBannerUrl}
							hoverBannerUrl={finalHoverBannerUrl}
							bannerColor={bannerColor}
							user={user}
							decorationSurface={decorationSurface}
							avatarUrl={finalAvatarUrl}
							hoverAvatarUrl={finalHoverAvatarUrl}
							isClickable={true}
							onAvatarClick={openMockProfile}
							data-flx="user.profile.profile-preview.profile-card-banner"
						/>
						<ProfileCardContent data-flx="user.profile.profile-preview.profile-card-content">
							<ProfileCardUserInfo
								displayName={displayName}
								user={previewUser}
								pronouns={pronouns}
								showUsername={true}
								isClickable={true}
								onDisplayNameClick={openMockProfile}
								onUsernameClick={openMockProfile}
								themeColors={previewUser.themeColors ?? null}
								belowUsername={
									<UserProfileBadges
										user={previewUser}
										profile={mockProfile}
										isModal={true}
										largeProfile
										data-flx="user.profile.profile-preview.user-profile-badges"
									/>
								}
								data-flx="user.profile.profile-preview.profile-card-user-info"
							/>
							<div className={styles.profileCustomStatus} data-flx="user.profile.profile-preview.profile-custom-status">
								<CustomStatusDisplay
									userId={hasPreviewStatus ? undefined : user.id}
									customStatus={hasPreviewStatus ? previewCustomStatus : undefined}
									className={styles.profileCustomStatusText}
									allowJumboEmoji
									maxLines={0}
									alwaysAnimate={shouldAutoplayProfileAnimations}
									data-flx="user.profile.profile-preview.profile-custom-status-text"
								/>
							</div>
							<UserProfilePreviewBio
								profile={mockProfile}
								onShowMore={openMockProfile}
								data-flx="user.profile.profile-preview.user-profile-preview-bio"
							/>
							<UserProfileTimezoneInfo
								profile={mockProfile}
								data-flx="user.profile.profile-preview.user-profile-timezone-info"
							/>
							{showMembershipInfo && (
								<UserProfileMembershipInfo
									profile={{...mockProfile, guild: selectedGuild, guildMember} as Profile}
									user={previewUser}
									data-flx="user.profile.profile-preview.user-profile-membership-info"
								/>
							)}
						</ProfileCardContent>
						{showMessageButton && (
							<ProfileCardFooter data-flx="user.profile.profile-preview.profile-card-footer">
								<ProfileMessageField
									user={previewUser}
									isCurrentUser={true}
									data-flx="user.profile.profile-preview.profile-message-field"
								/>
							</ProfileCardFooter>
						)}
					</ProfileCardLayout>
				</div>
			</FocusRing>
		);
	},
);
